import type {
  BrandingFeature,
  RoleRequirementsMandatoryQuestionsFeature,
  CatalogueProductFeature,
  Maybe,
} from 'src/graphql/generated';
/**
 * This type uses conditional types to determine the return type based on the value of the type parameter `T`
 * The `T` parameter represents a string literal type or a union of string literal types. It's used to make decisions about the return type based on the specific values of `T`.
 * If `T` extends the string literal type `'RoleRequirementsMandatoryQuestions'`, it returns the type `RoleRequirementsMandatoryQuestionsFeature`. This means that if `T` is equal to this specific string value, the return type will be `RoleRequirementsMandatoryQuestionsFeature`.
 * If the first condition is not met (i.e., `T` doesn't match `'RoleRequirementsMandatoryQuestions'`), it checks the second condition: If `T` extends the string literal type `'Branding'`, it returns the type `BrandingFeature`. This means that if `T` is equal to `'Branding'`, the return type will be `BrandingFeature`.
 * If neither of the above conditions is met, the default return type is `ProductFeature`.
 * */
type FeatureReturn<T> = T extends 'RoleRequirementsMandatoryQuestions'
  ? RoleRequirementsMandatoryQuestionsFeature
  : T extends 'Branding'
  ? BrandingFeature
  : CatalogueProductFeature;

/**
 * Function to search and return a particular feature
 * @param featureType Feature type to search for
 * @param productFeatures All product features
 * @returns Desired feature or undefined
 */
export const getProductFeatureByType = <T extends string>(
  featureType: T,
  productFeatures: Maybe<CatalogueProductFeature>[] | undefined,
): FeatureReturn<T> | undefined =>
  productFeatures?.find(
    (feature): feature is FeatureReturn<T> => feature?.type === featureType,
  );
